<template>
	<div v-if="show" class="agent-init-progress-panel" :class="{ 'fade-out': isFadingOut }">
		<div class="panel-container">
			<!-- 标题 -->
			<div class="panel-header">
				<h3 v-if="!hasError">{{ isMobile ? '初始化中...' : '智能体初始化中...' }}</h3>
				<h3 v-else class="error-title">{{ isMobile ? '初始化失败' : '智能体初始化失败' }}</h3>
			</div>

			<!-- 步骤列表 -->
			<div class="steps-container">
				<div
					v-for="(step, index) in steps"
					:key="step.key"
					class="step-item"
					:class="{
						'step-active': index === currentStep,
						'step-error': step.status === 'error'
					}">
					<!-- 状态图标 -->
					<div class="step-icon">
						<!-- Pending: 空心圆圈 -->
						<div v-if="step.status === 'pending'" class="icon-circle"></div>

						<!-- Loading: 旋转边框 -->
						<div v-else-if="step.status === 'loading'" class="icon-loading"></div>

						<!-- Success: 绿色对号 -->
						<div v-else-if="step.status === 'success'" class="icon-success">
							<svg viewBox="0 0 24 24" >
								<path
									fill="currentColor"
									d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
							</svg>
						</div>

						<!-- Error: 红色叉号 -->
						<div v-else-if="step.status === 'error'" class="icon-error">
							<svg viewBox="0 0 24 24" >
								<path
									fill="currentColor"
									d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
							</svg>
						</div>
					</div>

					<!-- 步骤信息 -->
					<div class="step-content">
						<div class="step-label">{{ step.label }}</div>
						<div v-if="step.error" class="step-error-msg">{{ step.error }}</div>
					</div>
				</div>
			</div>

		<!-- 当前步骤详细说明 - 始终占位 -->
		<div class="current-step-message">
			<span v-if="lastMessage">{{ lastMessage }}</span>
			<span v-else>&nbsp;</span>
		</div>

			<!-- 错误操作按钮 -->
			<div v-if="hasError" class="panel-actions">
				<button class="btn-retry" @click="handleRetry">
					<svg viewBox="0 0 24 24"  style="margin-right: 4px;">
						<path
							fill="currentColor"
							d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" />
					</svg>
					重试
				</button>
				<button class="btn-cancel" @click="handleCancel">取消</button>
			</div>
		</div>
	</div>
</template>

<script>
export default {
	name: 'AgentInitProgressPanel',
	props: {
		show: {
			type: Boolean,
			default: false
		},
		steps: {
			type: Array,
			required: true,
			// [{key, label, status, error}, ...]
			default: () => []
		},
		currentStep: {
			type: Number,
			default: 0
		},
		isMobile: {
			type: Boolean,
			default: false
		}
	},
	computed: {
		hasError() {
			return this.steps.some(step => step.status === 'error');
		}
	},
	data() {
		return {
			isFadingOut: false,
			lastMessage: '' // 保存最后一条消息
		};
	},
	watch: {
		show(newVal) {
			// 当面板重新显示时，重置渐隐状态和消息
			if (newVal) {
				this.isFadingOut = false;
				this.lastMessage = '';
			}
		},
		steps: {
			handler(newSteps) {
				// 更新提示消息
				this.updateMessage();

				// 检查是否所有步骤都完成
				const allComplete = newSteps.every(step => step.status === 'success');
				const completeStep = newSteps.find(step => step.key === 'complete');

				if (allComplete && completeStep && completeStep.status === 'success' && !this.isFadingOut) {
					// 开始渐隐
					this.isFadingOut = true;

					// 500ms后通知父组件关闭
					setTimeout(() => {
						this.$emit('close');
					}, 500);
				}
			},
			deep: true,
			immediate: true
		},
		currentStep() {
			// 当前步骤变化时也更新消息
			this.updateMessage();
		}
	},
	methods: {
		updateMessage() {
			if (this.currentStep >= 0 && this.currentStep < this.steps.length) {
				const step = this.steps[this.currentStep];

				// Loading 状态的消息
				if (step.status === 'loading') {
					const messages = {
						userInfo: '正在加载用户信息...',
						agentInfo: '正在获取智能体配置...',
						vncPod: '正在准备云电脑环境，首次可能需要 15 秒...',
						session: '正在初始化会话环境...',
						complete: '正在启动智能体...'
					};
					this.lastMessage = messages[step.key] || '';
				}
				// Error 状态的消息
				else if (step.status === 'error') {
					const errorMessages = {
						userInfo: '用户信息加载失败',
						agentInfo: '智能体配置获取失败',
						vncPod: '云电脑环境准备失败',
						session: '会话环境初始化失败',
						complete: '智能体启动失败'
					};
					this.lastMessage = step.error || errorMessages[step.key] || '初始化失败';
				}
				// Success 或其他状态保持当前消息不变
			}
		},
		handleRetry() {
			this.$emit('retry');
		},
		handleCancel() {
			this.$emit('cancel');
		}
	}
};
</script>

<style scoped>
.agent-init-progress-panel {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: rgba(255, 255, 255, 0.98);
	backdrop-filter: blur(8px);
	-webkit-backdrop-filter: blur(8px);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 10;
	animation: fadeIn 0.3s ease;
	transition: opacity 0.5s ease;
}

/* 🔥 移动端模式：固定定位覆盖整个屏幕 */
@media (max-width: 768px) {
	.agent-init-progress-panel {
		position: fixed !important;
		top: 0 !important;
		left: 0 !important;
		right: 0 !important;
		bottom: 0 !important;
		width: 100vw !important;
		height: 100vh !important;
		z-index: 10000 !important;
	}
}

.panel-overlay.fade-out {
	opacity: 0;
}

@keyframes fadeIn {
	from {
		opacity: 0;
	}
	to {
		opacity: 1;
	}
}

.panel-container {
	background: white;
	border-radius: 8px;
	box-shadow:
		0 2px 12px rgba(0, 0, 0, 0.08),
		0 0 0 1px rgba(0, 0, 0, 0.05);
	padding: 20px 24px;
	min-width: 340px;
	max-width: 400px;
	text-align: left;
}

@media (max-width: 768px) {
	.panel-container {
		min-width: auto;
		width: 85%;
		padding: 18px 20px;
		text-align: left;
	}
}

.panel-header {
	margin-bottom: 16px;
	text-align: center;
}

.panel-header h3 {
	margin: 0;
	font-size: 15px;
	font-weight: 600;
	color: #333;
	letter-spacing: 0.3px;
}

.panel-header .error-title {
	color: #f56c6c;
}

.steps-container {
	margin-bottom: 14px;
	text-align: left;
}

.step-item {
	display: flex;
	align-items: flex-start;
	margin-bottom: 10px;
	/* transition: all 0.2s ease; */ /* 禁用过渡，避免对齐问题 */
	min-height: 20px;
	text-align: left;
}

.step-item:last-child {
	margin-bottom: 0;
}

.step-active {
	/* transform: scale(1.01); */ /* 禁用缩放，避免对齐问题 */
}

.step-icon {
	width: 16px;
	height: 16px;
	flex-shrink: 0;
	margin-right: 10px;
	margin-top: 2px;
	padding: 0;
	overflow: visible;
}

/* 确保所有图标元素尺寸一致 */
.step-icon > * {
	width: 16px;
	height: 16px;
	display: block;
	box-sizing: border-box;
	margin: 0;
	padding: 0;
	flex-shrink: 0;
}

/* Pending: 空心圆圈 */
.icon-circle {
	border-radius: 50%;
	border: 2px solid #d1d5db;
	background: white;
	position: relative;
}

/* Loading: 旋转边框 */
.icon-loading {
	border-radius: 50%;
	border: 2px solid #e5e7eb;
	border-top-color: #409eff;
	background: white;
	position: relative;
	animation: spin 0.8s linear infinite;
}

@keyframes spin {
	0% {
		transform: rotate(0deg);
	}
	100% {
		transform: rotate(360deg);
	}
}

/* Success: 绿色对号 */
.icon-success {
	background: #67c23a;
	border-radius: 50%;
	border: 2px solid #67c23a;
	color: white;
	/* animation: scaleIn 0.2s ease; */ /* 禁用动画，避免对齐问题 */
	display: flex;
	align-items: center;
	justify-content: center;
	position: relative;
}

.icon-success svg {
	width: 10px;
	height: 10px;
	display: block;
}

@keyframes scaleIn {
	from {
		transform: scale(0);
	}
	to {
		transform: scale(1);
	}
}

/* Error: 红色叉号 */
.icon-error {
	background: #f56c6c;
	border-radius: 50%;
	border: 2px solid #f56c6c;
	color: white;
	/* animation: shake 0.4s ease; */ /* 禁用动画，避免对齐问题 */
	display: flex;
	align-items: center;
	justify-content: center;
	position: relative;
}

.icon-error svg {
	width: 10px;
	height: 10px;
	display: block;
}

@keyframes shake {
	0%,
	100% {
		transform: translateX(0);
	}
	25% {
		transform: translateX(-3px);
	}
	75% {
		transform: translateX(3px);
	}
}

.step-content {
	flex: 1;
	min-width: 0;
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	text-align: left;
}

.step-label {
	font-size: 13px;
	color: #333;
	line-height: 1.4;
	font-weight: 500;
	text-align: left;
}

.step-error .step-label {
	color: #f56c6c;
}

.step-error-msg {
	font-size: 12px;
	color: #f56c6c;
	margin-top: 2px;
	line-height: 1.3;
}

.current-step-message {
	background: #f0f9ff;
	border: 1px solid #bfdbfe;
	border-radius: 6px;
	padding: 8px 12px;
	font-size: 12px;
	color: #1e40af;
	margin-bottom: 14px;
	text-align: center;
	line-height: 1.4;
}

.panel-actions {
	display: flex;
	justify-content: center;
	gap: 10px;
	margin-top: 16px;
}

.panel-actions button {
	padding: 7px 18px;
	border-radius: 5px;
	font-size: 13px;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.2s ease;
	border: none;
	display: flex;
	align-items: center;
	justify-content: center;
}

.btn-retry {
	background: #409eff;
	color: white;
}

.btn-retry:hover {
	background: #66b1ff;
	transform: translateY(-1px);
	box-shadow: 0 2px 6px rgba(64, 158, 255, 0.25);
}

.btn-retry:active {
	transform: translateY(0);
}

.btn-cancel {
	background: white;
	color: #606266;
	border: 1px solid #dcdfe6;
}

.btn-cancel:hover {
	background: #f5f7fa;
	border-color: #c0c4cc;
}

.btn-cancel:active {
	background: #e4e7ed;
}
</style>
